import styled, { css, keyframes } from 'styled-components';

/* ───────── Keyframes ───────── */

const pop = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.55) rotate(-18deg);
    filter: blur(6px);
  }
  60% {
    opacity: 1;
    transform: scale(1.08) rotate(3deg);
    filter: blur(0);
  }
  100% {
    opacity: 1;
    transform: scale(1) rotate(0deg);
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.35),
      0 0 80px rgba(255, 255, 255, 0.2);
  }
  50% {
    box-shadow: 0 0 0 24px rgba(255, 255, 255, 0),
      0 0 80px rgba(255, 255, 255, 0.15);
  }
`;

const driftBg = keyframes`
  0%   { transform: translate3d(0,0,0); }
  50%  { transform: translate3d(-20px,-10px,0); }
  100% { transform: translate3d(0,0,0); }
`;

const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

/* ───────── Splash ───────── */

export const Splash = styled.div<{ $done: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: linear-gradient(135deg, #006633 0%, #00874a 60%, #00a65a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.55s ease, transform 0.55s cubic-bezier(0.65, 0, 0.35, 1),
    visibility 0s linear 0.55s;
  pointer-events: ${({ $done }) => ($done ? 'none' : 'auto')};

  ${({ $done }) =>
    $done
      ? css`
          opacity: 0;
          transform: scale(1.04);
          visibility: hidden;
        `
      : css`
          opacity: 1;
          transform: scale(1);
          visibility: visible;
          transition-delay: 0s;
        `}

  /* decorative soft orbs */
  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.45;
    animation: ${driftBg} 6s ease-in-out infinite;
  }
  &::before {
    width: 320px;
    height: 320px;
    background: #f38725;
    top: 15%;
    left: 12%;
  }
  &::after {
    width: 420px;
    height: 420px;
    background: #00a65a;
    bottom: 10%;
    right: 8%;
    animation-delay: 1.5s;
  }
`;

export const SplashLogoBox = styled.div`
  position: relative;
  width: 180px;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  animation: ${pulseGlow} 2s ease-in-out infinite;

  /* All variants stacked, toggled by .active */
  img {
    position: absolute;
    max-width: 100%;
    max-height: 100%;
    opacity: 0;
    transform: scale(0.85);
    transition: opacity 0.45s cubic-bezier(0.22, 1, 0.36, 1),
      transform 0.45s cubic-bezier(0.22, 1, 0.36, 1);
    filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.25));
    object-fit: contain;
  }

  img.active {
    opacity: 1;
    transform: scale(1);
  }

  img.variant-symbol {
    width: 110px;
    height: 110px;
  }

  img.variant-vertical {
    width: 160px;
    height: 160px;
  }

  img.variant-horizontal {
    width: 260px;
    height: auto;
  }
`;

export const SplashTagline = styled.div<{ $visible: boolean }>`
  position: absolute;
  bottom: 18%;
  left: 50%;
  transform: translateX(-50%)
    translateY(${({ $visible }) => ($visible ? '0' : '8px')});
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity 0.5s ease, transform 0.5s ease;
  color: rgba(255, 255, 255, 0.9);
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3em;
  text-transform: uppercase;
`;

/* ───────── Layout ───────── */

export const PageContainer = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  min-height: 100vh;
  width: 100vw;
  background: #f8fafc;
  font-family: 'Plus Jakarta Sans', sans-serif;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

export const LeftPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  background: #f8fafc;
  position: relative;

  @media (max-width: 968px) {
    min-height: 100vh;
  }
`;

export const FormWrap = styled.div`
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const BrandBlock = styled.div<{ $delay?: number }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  opacity: 0;
  animation: ${slideUp} 0.55s ease forwards;
  animation-delay: ${({ $delay }) => $delay || 0}ms;

  img {
    width: 180px;
    height: auto;
    margin-bottom: 12px;
  }

  h1 {
    margin: 0;
    font-size: 28px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.03em;
    line-height: 1.15;
  }

  p {
    margin: 0;
    font-size: 14px;
    color: #64748b;
    font-weight: 500;
  }
`;

export const FormBlock = styled.form<{ $delay?: number }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  opacity: 0;
  animation: ${slideUp} 0.55s ease forwards;
  animation-delay: ${({ $delay }) => $delay || 0}ms;
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 0;
  }

  .field-label {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    letter-spacing: -0.01em;
  }

  .error {
    color: #b91c1c;
    font-size: 12px;
    font-weight: 500;
    margin-top: 2px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;

export const InputWrapper = styled.div<{ hasError?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;

  .input-icon {
    position: absolute;
    left: 14px;
    color: ${({ hasError }) => (hasError ? '#b91c1c' : '#94a3b8')};
    font-size: 16px;
    pointer-events: none;
    transition: color 0.15s ease;
  }

  .eye-toggle {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    padding: 6px;
    border-radius: 6px;
    color: #94a3b8;
    cursor: pointer;
    display: inline-flex;

    &:hover {
      background: #f1f5f9;
      color: #006633;
    }
  }

  input {
    width: 100%;
    height: 44px;
    padding: 0 14px 0 42px;
    border: 1px solid ${({ hasError }) => (hasError ? '#fca5a5' : '#e2e8f0')};
    border-radius: 10px;
    font-size: 14px;
    font-family: inherit;
    color: #0f172a;
    background: #ffffff;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
    outline: none;

    &::placeholder {
      color: #94a3b8;
    }

    &:hover {
      border-color: ${({ hasError }) => (hasError ? '#fca5a5' : '#cbd5e1')};
    }

    &:focus {
      border-color: ${({ hasError }) => (hasError ? '#b91c1c' : '#006633')};
      box-shadow: 0 0 0 3px
        ${({ hasError }) =>
          hasError ? 'rgba(185, 28, 28, 0.1)' : 'rgba(0, 102, 51, 0.12)'};
    }

    &:focus + .input-icon,
    &:focus ~ .input-icon {
      color: ${({ hasError }) => (hasError ? '#b91c1c' : '#006633')};
    }
  }
`;

export const RowBetween = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

export const Check = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #475569;
  cursor: pointer;
  font-weight: 500;
  user-select: none;

  input {
    width: 16px;
    height: 16px;
    accent-color: #006633;
    cursor: pointer;
  }
`;

export const ForgotLink = styled.a`
  color: #006633;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: color 0.15s ease;

  &:hover {
    color: #00451f;
    text-decoration: underline;
  }
`;

export const SubmitButton = styled.button`
  width: 100%;
  height: 46px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #006633 0%, #00874a 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  font-family: inherit;
  letter-spacing: -0.01em;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.2s ease, filter 0.2s ease;
  margin-top: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 6px 16px rgba(0, 102, 51, 0.2);

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(0, 102, 51, 0.28);
    filter: brightness(1.05);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .spinner {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const Helper = styled.div<{ $delay?: number }>`
  margin-top: 4px;
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
  font-weight: 500;
  opacity: 0;
  animation: ${fadeIn} 0.5s ease forwards;
  animation-delay: ${({ $delay }) => $delay || 0}ms;

  a {
    color: #006633;
    font-weight: 600;
    text-decoration: none;

    &:hover {
      color: #00451f;
      text-decoration: underline;
    }
  }
`;

/* ───────── Right panel (brand) ───────── */

export const RightPanel = styled.div<{ $delay?: number }>`
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #006633 0%, #00874a 55%, #00a65a 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  color: #ffffff;

  opacity: 0;
  animation: ${fadeIn} 0.7s ease forwards;
  animation-delay: ${({ $delay }) => $delay || 0}ms;

  @media (max-width: 968px) {
    display: none;
  }

  /* decorative orbs */
  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.35;
    animation: ${driftBg} 8s ease-in-out infinite;
  }
  &::before {
    width: 360px;
    height: 360px;
    background: #f38725;
    top: -80px;
    right: -80px;
  }
  &::after {
    width: 460px;
    height: 460px;
    background: #00a65a;
    bottom: -120px;
    left: -100px;
    animation-delay: 2s;
  }

  /* subtle dot pattern */
  .pattern {
    position: absolute;
    inset: 0;
    background-image: radial-gradient(
      rgba(255, 255, 255, 0.08) 1px,
      transparent 1px
    );
    background-size: 22px 22px;
    pointer-events: none;
  }
`;

export const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 24px;

  img {
    width: 260px;
    height: auto;
    filter: drop-shadow(0 12px 32px rgba(0, 0, 0, 0.25));
    animation: ${pop} 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
    animation-delay: 150ms;
  }

  h2 {
    margin: 0;
    font-size: 26px;
    font-weight: 700;
    letter-spacing: -0.03em;
    line-height: 1.2;
  }

  p {
    margin: 0;
    font-size: 15px;
    line-height: 1.55;
    color: rgba(255, 255, 255, 0.82);
    font-weight: 500;
  }
`;

export const HeroStats = styled.div`
  position: relative;
  z-index: 1;
  margin-top: 32px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
  max-width: 420px;

  .stat {
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(8px);
    border-radius: 12px;
    padding: 12px 10px;
    text-align: center;
  }

  .stat strong {
    display: block;
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
  }

  .stat span {
    display: block;
    margin-top: 2px;
    font-size: 10px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.75);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }
`;
